// @ts-ignore

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  arrayUnion,
  getDocs,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

import type { AppDispatch, RootState } from "./store";
import ngeohash from "ngeohash";
import { GeoPoint } from "firebase/firestore";
import "firebase/compat/firestore";
import { GeoFirestore } from "geofirestore";
import { firebase } from "../firebase";

// ---------- Firebase refs ----------
const auth = getAuth();
const db = getFirestore();
const storage = getStorage();

// ---------- Types ----------
export type CleaningListing = {
  listingId: string;
  hostId: string;
  name?: string;
  category?: number | null;
  joinDate: string;
  isPublished: boolean;
  isFlagged: boolean;
  isComplete: boolean;
  isReviewed: boolean;
  inReview: boolean;
  isDraft: boolean;
  photos?: string[];
  videoUrl?: string;
  logoUrl?: string;
  createdAt?: number | null;
  updatedAt?: number | null;
  [key: string]: any;
  address?: {
    country?: string;
    street?: string;
    farmName?: string;
    neighborhood?: string;
    apt?: string;
    zip?: string;
    city?: string;
    county?: string;
    latitude?: number;
    longitude?: number;
  };
};

export type CleaningState = {
  hourAvailability: any;
  listings: CleaningListing[];
  currentListing: CleaningListing | null;
  blockedDurations: string[];
  locations: {
    country?: string;
    street?: string;
    farmName?: string;
    neighborhood?: string;
    apt?: string;
    zip?: string;
    city?: string;
    county?: string;
    latitude?: number | null;
    longitude?: number | null;
  }[];
  listingDiscounts: Record<string, Record<string, boolean>>;
  address: {
    street?: string;
    city?: string;
    province?: string;
    country?: string;
    latitude?: string;
    longitude?: string;
  }[];
  filters: Record<string, any>;
  unsubscribe?: () => void;
  loadingBlocked: boolean;
  errorBlocked: string | null;
  loading: boolean;
  error: string | null;
};

// ---------- Initial State ----------
const initialState: CleaningState = {
  listings: [],
  currentListing: null,
  blockedDurations: [],
  unsubscribe: undefined,
  loadingBlocked: false,
  errorBlocked: null,
  loading: false,
  error: null,
  locations: [],
  filters: {},
  address: [],
  listingDiscounts: {},
  hourAvailability: undefined
};




export const upsertCleaning = createAsyncThunk<
  CleaningListing,
  { listingId?: string; data: Partial<CleaningListing> },
  { rejectValue: string }
>("cleaning/upsertCleaning", async ({ listingId, data }, { rejectWithValue }) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    const firestoreRef = firebase.firestore();
    const geoFirestore = new GeoFirestore(firestoreRef as any);
    const geoCollection = geoFirestore.collection("cleaning");

    // Optionally attach a brandId if the user is a commerce partner (category 7).
    // All authenticated users can create listings regardless.
    const partnersSnap = await getDocs(
      query(
        collection(firestoreRef, "commercePartners"),
        where("userId", "==", user.uid),
        where("categoryId", "==", 7)
      )
    );
    const brandId = partnersSnap.empty ? null : partnersSnap.docs[0].id;

    const lat = Number(data.address?.latitude);
    const lng = Number(data.address?.longitude);
    const hasCoords = !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;

    const baseData: any = {
      ...data,
      ...(brandId && { brandId }),
      categoryId: 7,
      hostId:     user.uid,
      joinDate:   new Date().toISOString(),
      updatedAt:  firebase.firestore.FieldValue.serverTimestamp(),
      ...(hasCoords && {
        l: new firebase.firestore.GeoPoint(lat, lng),
        g: ngeohash.encode(lat, lng),
      }),
    };

    if (listingId) {
      const docRef = firestoreRef.collection("cleaning").doc(listingId);
      if (hasCoords) {
        await geoCollection.doc(listingId).set(baseData, { merge: true });
      } else {
        await docRef.update(baseData);
      }
      const snap = await docRef.get();
      return { listingId, ...snap.data() } as CleaningListing;
    }

    // Create a new listing — open to any authenticated user
    const newDocRef = hasCoords
      ? await geoCollection.add({
          ...baseData,
          createdAt:   firebase.firestore.FieldValue.serverTimestamp(),
          isPublished: false,
          isFlagged:   false,
          isComplete:  false,
          isReviewed:  false,
          inReview:    false,
          isDraft:     true,
        })
      : await firestoreRef.collection("cleaning").add({
          ...baseData,
          createdAt:   firebase.firestore.FieldValue.serverTimestamp(),
          isPublished: false,
          isFlagged:   false,
          isComplete:  false,
          isReviewed:  false,
          inReview:    false,
          isDraft:     true,
        });

    await newDocRef.update({ listingId: newDocRef.id });
    const snap = await newDocRef.get();
    return { listingId: newDocRef.id, ...snap.data() } as CleaningListing;

  } catch (err: any) {
    return rejectWithValue(err.message ?? "Failed to upsert cleaning listing");
  }
});



// ---------- Universal Media Upload (Photos or Video) ----------
export const upsertCleaningMedia = createAsyncThunk<
  { listingId: string; downloadURLs: string[] },
  { files: File[]; listingId?: string; type: "photo" | "video" },
  { rejectValue: string }
>(
  "cleaning/upsertCleaningMedia",
  async ({ files, listingId, type }, { rejectWithValue }) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not logged in");

      const hostId = currentUser.uid;
      const joinDate = new Date().toISOString();
      let id = listingId;

      const uploaded: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const name = file.name || `${type}-${Date.now()}-${i}`;
        const path = `cleaning/${id || "new"}/${type === "photo" ? "photos" : "video"}/${name}`;
        const storageRef = ref(storage, path);

        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        uploaded.push(url);
      }

      if (!id) {
        const docRef = await addDoc(collection(db, "cleaning"), {
          hostId,
          joinDate,
          photos: type === "photo" ? uploaded : [],
          videoUrl: type === "video" ? uploaded[0] : "",
          isPublished: false,
          isFlagged: false,
          isComplete: false,
          isReviewed: false,
          inReview: true,
          isDraft: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        id = docRef.id;
        await updateDoc(doc(db, "cleaning", id), { listingId: id });
      } else {
        const updateData =
          type === "photo"
            ? { photos: arrayUnion(...uploaded) }
            : { videoUrl: uploaded[0] };

        await updateDoc(doc(db, "cleaning", id), {
          ...updateData,
          updatedAt: serverTimestamp(),
        });
      }

      return { listingId: id, downloadURLs: uploaded };
    } catch (err: any) {
      console.error("🔥 upsertCleaningMedia error:", err);
      return rejectWithValue(err.message || "Media upload failed");
    }
  }
);

export type WebFile = File;

// ---------- Video Upload ----------
export const upsertCleaningVideo = createAsyncThunk<
  string,
  { listingId: string; video: File | Blob },
  { state: RootState; rejectValue: string }
>(
  "cleaning/upsertCleaningVideo",
  async ({ listingId, video }, { dispatch, rejectWithValue }) => {
    try {
      const name = (video as any).name || `video-${Date.now()}.mp4`;
      const path = `cleaning/${listingId}/video/${name}`;
      const storageRef = ref(storage, path);

      await uploadBytes(storageRef, video);
      const url = await getDownloadURL(storageRef);

      await dispatch(upsertCleaning({ listingId, data: { videoUrl: url } }));

      return url;
    } catch (e: any) {
      return rejectWithValue(e.message || "Video upload failed");
    }
  }
);

// ---------- Photo Upload ----------
export const upsertCleaningPhoto = createAsyncThunk<
  string[],
  { listingId: string; photos: WebFile[] },
  { state: RootState; rejectValue: string }
>(
  "cleaning/upsertCleaningPhoto",
  async ({ listingId, photos }, { dispatch, getState, rejectWithValue }) => {
    try {
      const uploadedUrls: string[] = [];

      for (let i = 0; i < photos.length; i++) {
        const f = photos[i];
        const name = f.name || `photo-${Date.now()}-${i}`;
        const path = `cleaning/${listingId}/photos/${name}`;
        const storageRef = ref(storage, path);

        await uploadBytes(storageRef, f);
        const url = await getDownloadURL(storageRef);
        uploadedUrls.push(url);
      }

      const existing = getState().cleaning.currentListing?.photos ?? [];
      const updated = [...existing, ...uploadedUrls];

      await dispatch(upsertCleaning({ listingId, data: { photos: updated } }));
      return updated;
    } catch (e: any) {
      return rejectWithValue(e.message || "Photo upload failed");
    }
  }
);

// ---------- Delete Media ----------
export const deleteCleaningMedia = createAsyncThunk<
  { deletedPhoto?: string; deletedVideo?: boolean },
  { listingId: string; photoUrl?: string },
  { state: RootState; rejectValue: string }
>(
  "cleaning/deleteCleaningMedia",
  async ({ listingId, photoUrl }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState().cleaning;
      const currentListing = state.currentListing;
      if (!currentListing) throw new Error("Listing not found");

      const updates: Partial<CleaningListing> = {};
      let deletedPhoto: string | undefined;
      let deletedVideo = false;

      if (photoUrl) {
        try {
          const photoRef = ref(storage, photoUrl);
          await deleteObject(photoRef);
          const newPhotos = (currentListing.photos || []).filter((p) => p !== photoUrl);
          updates.photos = newPhotos;
          deletedPhoto = photoUrl;
        } catch (err) {
          console.warn("⚠️ Failed to delete photo from storage:", err);
        }
      }

      if (currentListing.videoUrl) {
        try {
          const videoRef = ref(storage, currentListing.videoUrl);
          await deleteObject(videoRef);
          updates.videoUrl = undefined;
          deletedVideo = true;
        } catch (err) {
          console.warn("⚠️ Failed to delete video from storage:", err);
        }
      }

      if (Object.keys(updates).length > 0) {
        await dispatch(upsertCleaning({ listingId, data: updates }));
      }

      return { deletedPhoto, deletedVideo };
    } catch (e: any) {
      console.error("🔥 deleteCleaningMedia error:", e);
      return rejectWithValue(e.message || "Failed to delete media");
    }
  }
);

// ---------- Delete Listing ----------
export const deleteCleaning = createAsyncThunk<string, string>(
  "cleaning/deleteCleaning",
  async (listingId) => {
    const docRef = doc(db, "cleaning", listingId);
    await deleteDoc(docRef);
    return listingId;
  }
);

// ---------- Listen to All Published ----------
export const listenToPublishedCleanings =
  () => (dispatch: AppDispatch) => {
    const q = query(
      collection(db, "cleaning"),
      where("isPublished", "==", true)
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const listings: CleaningListing[] = snap.docs.map((docSnap) => {
          const raw = docSnap.data() || {};
          return {
            listingId: docSnap.id,
            hostId: raw.hostId ?? "",
            joinDate: raw.joinDate ?? new Date().toISOString(),
            name: raw.name ?? "",
            category: raw.category ?? null,
            isPublished: true,
            isFlagged: raw.isFlagged ?? false,
            isComplete: raw.isComplete ?? false,
            isReviewed: raw.isReviewed ?? false,
            inReview: raw.inReview ?? false,
            isDraft: raw.isDraft ?? false,
            photos: raw.photos ?? [],
            address: raw.address ?? {},
            videoUrl: raw.videoUrl ?? undefined,
            logoUrl: raw.logoUrl ?? undefined,
            ...raw,
            createdAt: raw.createdAt?.toMillis?.() || null,
            updatedAt: raw.updatedAt?.toMillis?.() || null,
          } as CleaningListing;
        });

        dispatch(cleaningSlice.actions.setListings(listings));
      },
      (error) => {
        console.error("❌ Firestore error:", error);
        dispatch(cleaningSlice.actions.setError(error.message));
      }
    );

    dispatch(cleaningSlice.actions.setUnsubscribe(unsubscribe));
    return unsubscribe;
  };

// ---------- Listen by Host ----------
export const listenToCleaningsByHost =
  (hostId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!hostId) {
        console.warn("⚠️ listenToCleaningsByHost skipped — no hostId provided");
        return;
      }

      const existingUnsub = getState().cleaning.unsubscribe;
      if (existingUnsub) existingUnsub();

      const dbRef = getFirestore();

      const q = query(
        collection(dbRef, "cleaning"),
        where("hostId", "==", hostId),
        where("isPublished", "==", true),
        where("categoryId", "==", 7)
      );

      const unsubscribe = onSnapshot(
        q,
        (snap) => {
          const listings = snap.docs.map((docSnap) => {
            const data = docSnap.data() || {};
            return {
              listingId: docSnap.id,
              ...data,
              createdAt:
                data.createdAt?.toMillis?.() ??
                (typeof data.createdAt === "number" ? data.createdAt : null),
              updatedAt:
                data.updatedAt?.toMillis?.() ??
                (typeof data.updatedAt === "number" ? data.updatedAt : null),
            } as CleaningListing;
          });

          dispatch(setListings(listings));
        },
        (err) => {
          console.error("❌ listenToCleaningsByHost error:", err.message);
          dispatch(setError(err.message));
        }
      );

      dispatch(setUnsubscribe(unsubscribe));
      return unsubscribe;
    } catch (err: any) {
      console.error("🔥 listenToCleaningsByHost FAILED:", err.message);
      dispatch(setError(err.message));
    }
  };

// ---------- Listen Nearby (Geo) ----------
export const listenToNearbyCleanings =
  (lat: number, lng: number, radiusKm: number = 2) =>
  (dispatch: AppDispatch) => {
    try {
      // @ts-ignore
      const geoFirestore = new GeoFirestore(getFirestore());
      const geoCollection = geoFirestore.collection("cleaning");

      const query = geoCollection.near({
        center: new GeoPoint(lat, lng),
        radius: radiusKm,
      });

      const unsubscribe = query.onSnapshot(
        (snapshot: any) => {
          const listings = snapshot.docs.map((doc: any) => ({
            id: doc.id,
            ...doc.data(),
          }));
          dispatch(cleaningSlice.actions.setListings(listings as any));
        },
        (error: any) => {
          console.error("🔥 listenToNearbyCleanings error:", error);
          dispatch(cleaningSlice.actions.setError(error.message));
        }
      );

      return unsubscribe;
    } catch (err: any) {
      console.error("❌ listenToNearbyCleanings setup failed:", err.message);
    }
  };

// ---------- Listen to Single Listing ----------
export const listenToCleaningData =
  (listingId: string) => (dispatch: AppDispatch) => {
    const docRef = doc(db, "cleaning", listingId);

    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const raw = docSnap.data() || {};
          dispatch(
            cleaningSlice.actions.setCurrentListing({
              listingId: docSnap.id,
              hostId: raw.hostId ?? "",
              joinDate: raw.joinDate ?? new Date().toISOString(),
              name: raw.name ?? "",
              category: raw.category ?? null,
              isPublished: raw.isPublished ?? false,
              isFlagged: raw.isFlagged ?? false,
              isComplete: raw.isComplete ?? false,
              isReviewed: raw.isReviewed ?? false,
              inReview: raw.inReview ?? false,
              photos: raw.photos ?? [],
              videoUrl: raw.videoUrl ?? undefined,
              logoUrl: raw.logoUrl ?? undefined,
              ...raw,
              createdAt: raw.createdAt?.toMillis?.() || null,
              updatedAt: raw.updatedAt?.toMillis?.() || null,
            } as CleaningListing)
          );
        } else {
          dispatch(cleaningSlice.actions.setCurrentListing(null));
        }
      },
      (error) => {
        dispatch(cleaningSlice.actions.setError(error.message));
      }
    );

    return unsubscribe;
  };

// ---------- Listen to Host's Own Listings ----------
export const listenToHostCleanings =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const user = getAuth().currentUser;
    if (!user || !user.uid) {
      console.warn("⚠️ listenToHostCleanings skipped — user not authenticated");
      dispatch(setListings([]));
      return;
    }

    const hostId = user.uid;
    if (typeof hostId !== "string" || !hostId.trim()) {
      console.warn("⚠️ listenToHostCleanings skipped — invalid hostId");
      dispatch(setListings([]));
      return;
    }

    const existingUnsub = getState().cleaning.unsubscribe;
    if (existingUnsub) {
      console.log("🟡 Removing previous cleaning listener");
      existingUnsub();
      dispatch(setUnsubscribe(undefined));
    }

    const q = query(collection(db, "cleaning"), where("hostId", "==", hostId));

    const unsubscribeFn = onSnapshot(
      q,
      (snap: { docs: { id: string; data: () => { [key: string]: any } }[] }) => {
        const listings = snap.docs
          .map((docSnap) => {
            const raw = docSnap.data() || {};
            return {
              listingId: docSnap.id,
              ...raw,
              hostId: raw.hostId ?? "",
              joinDate: raw.joinDate ?? new Date().toISOString(),
              name: raw.name ?? "",
              category: raw.category ?? null,
              isPublished: raw.isPublished ?? false,
              isFlagged: raw.isFlagged ?? false,
              isComplete: raw.isComplete ?? false,
              isReviewed: raw.isReviewed ?? false,
              inReview: raw.inReview ?? false,
              isDraft: raw.isDraft ?? false,
              photos: raw.photos ?? [],
              address: raw.address ?? {},
              videoUrl: raw.videoUrl ?? undefined,
              logoUrl: raw.logoUrl ?? undefined,
              createdAt: raw.createdAt?.toMillis?.() || null,
              updatedAt: raw.updatedAt?.toMillis?.() || null,
            };
          })
          .filter((l) => l.hostId === hostId);

        console.log(`📡 Host ${hostId} cleaning listings received: ${listings.length}`);
        dispatch(setListings(listings));
      },
      (error: { message: string }) => {
        console.error("❌ listenToHostCleanings Firestore error:", error.message);
        dispatch(setError(error.message));
      }
    );

    dispatch(setUnsubscribe(unsubscribeFn));
  };

// ---------- Listen to Multiple by IDs ----------
export const listenToMultiCleanings =
  (ids: string[]) => (dispatch: AppDispatch) => {
    if (!ids?.length) return;

    const unsubscribers: (() => void)[] = [];

    for (let i = 0; i < ids.length; i += 10) {
      const group = ids.slice(i, i + 10);

      const q = query(
        collection(db, "cleaning"),
        where("listingId", "in", group),
        where("isPublished", "==", true)
      );

      const unsubscribe = onSnapshot(
        q,
        (snap) => {
          const results: CleaningListing[] = snap.docs.map((docSnap) => {
            const raw = docSnap.data() || {};
            return {
              listingId: docSnap.id,
              hostId: raw.hostId ?? "",
              joinDate: raw.joinDate ?? new Date().toISOString(),
              name: raw.name ?? "",
              category: raw.category ?? null,
              isPublished: raw.isPublished ?? false,
              isFlagged: raw.isFlagged ?? false,
              isComplete: raw.isComplete ?? false,
              isReviewed: raw.isReviewed ?? false,
              photos: raw.photos ?? [],
              videoUrl: raw.videoUrl ?? undefined,
              logoUrl: raw.logoUrl ?? undefined,
              ...raw,
              createdAt: raw.createdAt?.toMillis?.() || null,
              updatedAt: raw.updatedAt?.toMillis?.() || null,
            } as CleaningListing;
          });

          dispatch(cleaningSlice.actions.setListings(results));
        },
        (error) => {
          console.error("❌ listenToMultiCleanings error:", error.message);
          dispatch(cleaningSlice.actions.setError(error.message));
        }
      );

      unsubscribers.push(unsubscribe);
    }

    return () => {
      unsubscribers.forEach((u) => u());
    };
  };

// ---------- Listen to In-Review ----------
export const listenToCleaningsInReview =
  () => (dispatch: AppDispatch) => {
    const q = query(
      collection(db, "cleaning"),
      where("inReview", "==", true)
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const listings: CleaningListing[] = snap.docs.map((docSnap) => {
          const raw = docSnap.data() || {};
          return {
            listingId: docSnap.id,
            hostId: raw.hostId ?? "",
            joinDate: raw.joinDate ?? new Date().toISOString(),
            name: raw.name ?? "",
            category: raw.category ?? null,
            isPublished: true,
            isFlagged: raw.isFlagged ?? false,
            isComplete: raw.isComplete ?? false,
            isReviewed: raw.isReviewed ?? false,
            inReview: raw.inReview ?? false,
            isDraft: raw.isDraft ?? true,
            photos: raw.photos ?? [],
            address: raw.address ?? {},
            videoUrl: raw.videoUrl ?? undefined,
            logoUrl: raw.logoUrl ?? undefined,
            ...raw,
            createdAt: raw.createdAt?.toMillis?.() || null,
            updatedAt: raw.updatedAt?.toMillis?.() || null,
          } as CleaningListing;
        });

        dispatch(cleaningSlice.actions.setListings(listings));
      },
      (error) => {
        console.error("❌ Firestore error:", error);
        dispatch(cleaningSlice.actions.setError(error.message));
      }
    );

    dispatch(cleaningSlice.actions.setUnsubscribe(unsubscribe));
    return unsubscribe;
  };

// ---------- Listen to Available Locations ----------
export const listenToCleaningLocations = () => (dispatch: AppDispatch) => {
  const q = collection(db, "cleaning");

  onSnapshot(
    q,
    (snap) => {
      const seen = new Set<string>();
      const locations: {
        country?: string;
        street?: string;
        farmName?: string;
        neighborhood?: string;
        apt?: string;
        zip?: string;
        city?: string;
        county?: string;
        latitude?: number | null;
        longitude?: number | null;
      }[] = [];

      snap.forEach((docSnap) => {
        const data = docSnap.data() as any;
        const addr = data.address;

        const geoPoint = data.l;
        const latitude = geoPoint?.latitude ?? (addr?.latitude ? Number(addr.latitude) : null);
        const longitude = geoPoint?.longitude ?? (addr?.longitude ? Number(addr.longitude) : null);

        if (addr) {
          const key = `${addr.city || ""}-${addr.country || ""}-${addr.street || ""}`;
          if (!seen.has(key)) {
            seen.add(key);
            locations.push({
              country: addr.country ?? "",
              street: addr.street ?? "",
              farmName: addr.farmName ?? "",
              neighborhood: addr.neighborhood ?? "",
              apt: addr.apt ?? "",
              zip: addr.zip ?? "",
              city: addr.city ?? "",
              county: addr.county ?? "",
              latitude,
              longitude,
            });
          }
        }
      });

      dispatch(cleaningSlice.actions.setLocations(locations));
    },
    (error) => {
      console.error("❌ listenToCleaningLocations error:", error);
      dispatch(cleaningSlice.actions.setError(error.message));
    }
  );
};

// ---------- Discounts ----------
export const upsertCleaningDiscount = createAsyncThunk<
  { listingId: string; updatedFlags: Record<string, boolean> },
  {
    listingId: string;
    type: "is5" | "is10" | "is25" | "is50" | "is80";
    active: boolean;
  },
  { rejectValue: string }
>(
  "cleaning/upsertCleaningDiscount",
  async ({ listingId, type, active }, { rejectWithValue }) => {
    try {
      const docRef = doc(db, "cleaning", listingId);

      const discounts = {
        is5: false,
        is10: false,
        is25: false,
        is50: false,
        is80: false,
      };

      discounts[type] = active;

      await updateDoc(docRef, {
        discounts,
        discountUpdatedAt: serverTimestamp(),
      });

      return { listingId, updatedFlags: discounts };
    } catch (error: any) {
      console.error("🔥 upsertCleaningDiscount error:", error);
      return rejectWithValue(error.message || "Failed to apply discount");
    }
  }
);

export const listenToAllCleaningDiscounts =
  () => (dispatch: AppDispatch) => {
    const colRef = collection(db, "cleaning");

    const unsubscribe = onSnapshot(
      colRef,
      (snap) => {
        const discountsMap: Record<string, Record<string, boolean>> = {};

        snap.forEach((docSnap) => {
          const data = docSnap.data();
          if (data?.discounts) {
            discountsMap[docSnap.id] = data.discounts;
          }
        });

        dispatch(setListingDiscounts(discountsMap));
      },
      (error) => {
        console.error("❌ listenToAllCleaningDiscounts error:", error);
      }
    );

    return unsubscribe;
  };

// ---------- Slice ----------
const cleaningSlice = createSlice({
  name: "cleaning",
  initialState,
  reducers: {
    setListings: (state, action: PayloadAction<CleaningListing[]>) => {
      state.listings = action.payload;
    },
    setCurrentListing: (state, action: PayloadAction<CleaningListing | null>) => {
      state.currentListing = action.payload;
    },
    setBlockedDurations: (state, action: PayloadAction<string[]>) => {
      state.blockedDurations = action.payload;
    },
    setUnsubscribe: (state, action: PayloadAction<(() => void) | undefined>) => {
      state.unsubscribe = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setLocations: (
      state,
      action: PayloadAction<
        {
          country?: string;
          street?: string;
          farmName?: string;
          neighborhood?: string;
          apt?: string;
          zip?: string;
          city?: string;
          county?: string;
          latitude?: number | null;
          longitude?: number | null;
        }[]
      >
    ) => {
      state.locations = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setListingDiscounts: (
      state,
      action: PayloadAction<Record<string, Record<string, boolean>>>
    ) => {
      state.listingDiscounts = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(upsertCleaning.fulfilled, (state, action) => {
      const idx = state.listings.findIndex(
        (l) => l.listingId === action.payload.listingId
      );
      if (idx !== -1) {
        state.listings[idx] = action.payload;
        if (state.currentListing?.listingId === action.payload.listingId) {
          state.currentListing = action.payload;
        }
      } else {
        state.listings.push(action.payload);
      }
    });

    builder.addCase(deleteCleaning.fulfilled, (state, action) => {
      state.listings = state.listings.filter(
        (l) => l.listingId !== action.payload
      );
      if (state.currentListing?.listingId === action.payload) {
        state.currentListing = null;
      }
    });

    builder.addCase(upsertCleaningDiscount.fulfilled, (state, action) => {
      const { listingId, updatedFlags } = action.payload;
      const idx = state.listings.findIndex((l) => l.listingId === listingId);

      if (idx !== -1) {
        state.listings[idx] = {
          ...state.listings[idx],
          discounts: updatedFlags,
        };
      }

      if (state.currentListing?.listingId === listingId) {
        state.currentListing = {
          ...state.currentListing,
          discounts: updatedFlags,
        };
      }
    });
  },
});

export const {
  setListings,
  setCurrentListing,
  setBlockedDurations,
  setUnsubscribe,
  setLocations,
  setListingDiscounts,
  setError,
  setFilters,
  clearFilters,
} = cleaningSlice.actions;

export default cleaningSlice.reducer;
